/**
 * Utility functions for calculating plant statistics and metrics.
 * These functions are used to compute aggregated values like revenue, costs, margins, and quantities
 * for products associated with a plant location.
 */

import { Currency } from '../types/api/Currency/Currency';

/**
 * Sums numeric values from an array of items.
 * @param ts - Array of items to sum
 * @param value - Function to extract the numeric value from each item
 * @returns The sum of all values as a number
 */
export function sumNumber<T>(ts: T[], value: (t: T) => number): number {
  return ts.reduce((acc, t) => acc + value(t), 0);
}

/**
 * Sums Currency values from an array of items using Dinero-precision arithmetic.
 * @param ts - Array of items to sum
 * @param value - Function to extract the Currency from each item
 * @returns The sum of all values as a Currency
 */
export function sumCurrency<T>(ts: T[], value: (t: T) => Currency): Currency {
  const values = ts.map(value);
  if (values.length === 0) {
    return Currency.zero();
  }
  return values[0].add(...values.slice(1)) ?? Currency.zero();
}

/**
 * Sums numeric values from an array of items and returns formatted string.
 * @param ts - Array of items to sum
 * @param value - Function to extract the numeric value from each item
 * @returns The sum formatted to 2 decimal places as a string
 */
export function sum<T>(ts: T[], value: (t: T) => number): string {
  return sumNumber(ts, value).toFixed(2);
}

/**
 * Calculates a weighted average for an array of items.
 * The weighted average is calculated as: sum(value * quantity) / sum(quantity)
 *
 * @param ts - Array of items to calculate weighted average for
 * @param sumFn - Function to extract the value to be weighted
 * @param quantityFn - Function to extract the quantity (weight) for each item
 * @returns The weighted average formatted to 2 decimal places as a string, or "0.00" if array is empty or total quantity is 0
 */
export function weightAverage<T>(
  ts: T[],
  sumFn: (t: T) => number,
  quantityFn: (t: T) => number,
): string {
  if (ts.length === 0) {
    return '0.00';
  }
  const s = ts.reduce((acc, t) => acc + sumFn(t) * quantityFn(t), 0);
  const quantity = ts.reduce((acc, t) => acc + quantityFn(t), 0);
  if (quantity === 0) {
    return '0.00';
  }
  return (s / quantity).toFixed(2);
}

/**
 * Calculates a weighted average and formats it as a money string with $ prefix.
 *
 * @param ts - Array of items to calculate weighted average for
 * @param valueFn - Function to extract the monetary value to be weighted
 * @param quantityFn - Function to extract the quantity (weight) for each item
 * @returns The weighted average formatted as a money string (e.g., "$123.45")
 */
export function weightAverageMoney<T>(
  ts: T[],
  valueFn: (t: T) => number,
  quantityFn: (t: T) => number,
): string {
  return `$${weightAverage(ts, valueFn, quantityFn)}`;
}
